import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    query,
    orderBy,
    onSnapshot,
    serverTimestamp,
    where
} from 'firebase/firestore';
import { db } from '../firebase';
import { v4 as uuidv4 } from 'uuid';

const ALERTS_COLLECTION = 'alerts';

/**
 * Get all alerts with real-time updates
 * @param {Function} callback - Callback function to receive alerts updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAlerts = (callback) => {
    const alertsRef = collection(db, ALERTS_COLLECTION);
    const q = query(alertsRef, orderBy('createdAt', 'desc'));

    return onSnapshot(q, (snapshot) => {
        const alerts = snapshot.docs.map(doc => ({
            ...doc.data(),
            firestoreId: doc.id
        }));
        callback(alerts);
    }, (error) => {
        console.error('Error subscribing to alerts:', error);
        callback([]);
    });
};

/**
 * Get all active alerts
 * @returns {Promise<Array>}
 */
export const getActiveAlerts = async () => {
    try {
        const alertsRef = collection(db, ALERTS_COLLECTION);
        const q = query(
            alertsRef,
            where('isActive', '==', true),
            orderBy('publishedAt', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            ...doc.data(),
            firestoreId: doc.id
        }));
    } catch (error) {
        console.error('Error getting active alerts:', error);
        throw error;
    }
};

/**
 * Get all alerts
 * @returns {Promise<Array>}
 */
export const getAllAlerts = async () => {
    try {
        const alertsRef = collection(db, ALERTS_COLLECTION);
        const q = query(alertsRef, orderBy('createdAt', 'desc'));

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            ...doc.data(),
            firestoreId: doc.id
        }));
    } catch (error) {
        console.error('Error getting alerts:', error);
        throw error;
    }
};

/**
 * Create a new alert
 * @param {Object} alertData - Alert data
 * @param {string} alertData.message - Alert message
 * @param {string} alertData.createdBy - User ID of creator
 * @param {boolean} alertData.isActive - Whether alert is active
 * @returns {Promise<Object>} Created alert
 */
export const createAlert = async ({ message, createdBy, isActive = false }) => {
    try {
        const now = new Date().toISOString();
        const alertId = uuidv4();

        const newAlert = {
            id: alertId,
            message: message.trim(),
            createdBy,
            isActive,
            createdAt: now,
            publishedAt: isActive ? now : null
        };

        const docRef = await addDoc(collection(db, ALERTS_COLLECTION), newAlert);

        return {
            ...newAlert,
            firestoreId: docRef.id
        };
    } catch (error) {
        console.error('Error creating alert:', error);
        throw error;
    }
};

/**
 * Update an existing alert
 * @param {string} firestoreId - Firestore document ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateAlert = async (firestoreId, updates) => {
    try {
        const alertRef = doc(db, ALERTS_COLLECTION, firestoreId);
        const updateData = { ...updates };

        // If activating the alert and it doesn't have a publishedAt, set it
        if (updates.isActive && !updates.publishedAt) {
            updateData.publishedAt = new Date().toISOString();
        }

        // If deactivating, don't change publishedAt
        await updateDoc(alertRef, updateData);
    } catch (error) {
        console.error('Error updating alert:', error);
        throw error;
    }
};

/**
 * Toggle alert active status
 * @param {string} firestoreId - Firestore document ID
 * @param {boolean} currentStatus - Current active status
 * @returns {Promise<void>}
 */
export const toggleAlertStatus = async (firestoreId, currentStatus) => {
    try {
        const alertRef = doc(db, ALERTS_COLLECTION, firestoreId);
        const newStatus = !currentStatus;
        const updates = {
            isActive: newStatus
        };

        // Set publishedAt when activating for the first time
        if (newStatus) {
            updates.publishedAt = new Date().toISOString();
        }

        await updateDoc(alertRef, updates);
    } catch (error) {
        console.error('Error toggling alert status:', error);
        throw error;
    }
};

/**
 * Delete an alert
 * @param {string} firestoreId - Firestore document ID
 * @returns {Promise<void>}
 */
export const deleteAlert = async (firestoreId) => {
    try {
        const alertRef = doc(db, ALERTS_COLLECTION, firestoreId);
        await deleteDoc(alertRef);
    } catch (error) {
        console.error('Error deleting alert:', error);
        throw error;
    }
};
